import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import axios from "axios";

import { baseURL } from "../utils/common";

const ORDERS_URL = `${baseURL}/orders/admin/orders/`;
const ORDER_CREATE_URL = `${baseURL}/orders/admin/order/create/`;
const ORDER_DETAILS_URL = (id) => `${baseURL}/orders/admin/order/${id}/details/`;
const ORDER_UPDATE_URL = (id) => `${baseURL}/orders/admin/order/${id}/update/`;
const ORDER_DELETE_URL = (id) => `${baseURL}/orders/admin/order/${id}/delete/`;

const ITEMS_URL = `${baseURL}/shop/admin/items/`;

const formatPrice = (value) => {
    return `${Number(value || 0).toLocaleString("ru-RU")} ₸`;
};

const getStatusClass = (status) => {
    if (status === "Создан") return "order-status-created";

    if (status === "Товары проверены") return "order-status-items";

    if (status === "Запрошена оплата") return "order-status-payment-requested";
    if (status === "Счет выставлен") return "order-status-payment-requested";
    if (status === "Оплата запрошена") return "order-status-payment-requested";

    if (status === "Оплачен") return "order-status-paid";
    if (status === "Оплата подтверждена") return "order-status-paid";

    if (status === "Документы запрошены") return "order-status-docs-requested";

    if (status === "Документы получены") return "order-status-docs-ready";

    if (status === "Накладная прикреплена") return "order-status-delivery-note";

    if (status === "Отправлен") return "order-status-sent";

    if (status === "ЭСФ выставлена") return "order-status-esf";

    if (status === "Закончен") return "order-status-finished";

    return "order-status-default";
};

const getText = (value, lang = "ru") => {
    if (!value) return "—";
    if (typeof value === "string") return value || "—";

    return value[lang] || value.ru || "—";
};

const getFieldOptions = (fields, key) => {
    return fields?.[key]?.options || [];
};

export const OrdersPage = () => {
    const history = useNavigate();

    const [orders, setOrders] = useState([]);
    const [filters, setFilters] = useState(null);

    const [count, setCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [nextOffset, setNextOffset] = useState(null);

    const [tab, setTab] = useState("all");

    const [q, setQ] = useState("");
    const [orderStatus, setOrderStatus] = useState("");
    const [clientId, setClientId] = useState("");
    const [clientType, setClientType] = useState("");
    const [managerId, setManagerId] = useState("");
    const [itemQ, setItemQ] = useState("");
    const [shopItemId, setShopItemId] = useState("");
    const [minSum, setMinSum] = useState("");
    const [maxSum, setMaxSum] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [hasInvoice, setHasInvoice] = useState("");
    const [hasPayment, setHasPayment] = useState("");
    const [hasDeliveryNote, setHasDeliveryNote] = useState("");
    const [ordering, setOrdering] = useState("-id");
    const [limit, setLimit] = useState(20);

    const [filtersOpen, setFiltersOpen] = useState(false);

    const [loading, setLoading] = useState(true);
    const [moreLoading, setMoreLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const [deleteModal, setDeleteModal] = useState({
        open: false,
        id: null,
        title: "",
        text: "",
        loading: false,
    });

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createFields, setCreateFields] = useState({});
    const [createLoading, setCreateLoading] = useState(false);
    const [createSaving, setCreateSaving] = useState(false);
    const [createError, setCreateError] = useState("");

    const [createForm, setCreateForm] = useState({
        client_id: "",
        manager_id: "",
        pay_sum: "",
        recalculate_pay_sum: true,
    });

    const [clientSearch, setClientSearch] = useState("");
    const [selectedClient, setSelectedClient] = useState(null);

    const [invoiceFile, setInvoiceFile] = useState(null);
    const [paymentFile, setPaymentFile] = useState(null);
    const [deliveryNoteFile, setDeliveryNoteFile] = useState(null);

    const [priceMode, setPriceMode] = useState("retail");
    const [cartItems, setCartItems] = useState([]);

    const [catalogItems, setCatalogItems] = useState([]);
    const [catalogFilters, setCatalogFilters] = useState(null);
    const [catalogSearch, setCatalogSearch] = useState("");
    const [catalogCategoryId, setCatalogCategoryId] = useState("");
    const [catalogSubCategoryId, setCatalogSubCategoryId] = useState("");
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [catalogHasMore, setCatalogHasMore] = useState(false);
    const [catalogNextOffset, setCatalogNextOffset] = useState(null);

    const goToOrderDetails = (orderId) => {
        history(`/orders/${orderId}`);
    };

    const getCartItemPrice = (item) => {
        if (priceMode === "opt") {
            return Number(item.opt_price || item.price || 0);
        }

        return Number(item.price || 0);
    };

    const getCartTotal = () => {
        return cartItems.reduce((sum, item) => {
            return sum + getCartItemPrice(item) * Number(item.quantity || 0);
        }, 0);
    };

    const getClientOptions = () => {
        return getFieldOptions(createFields, "client_id");
    };

    const getFilteredClients = () => {
        const search = clientSearch.trim().toLowerCase();

        if (!search) {
            return [];
        }

        return getClientOptions()
            .filter((client) => {
                const label = String(client.label || "").toLowerCase();
                const clientTypeValue = String(client.client_type || "").toLowerCase();
                const binCode = String(client.bin_code || "").toLowerCase();

                return (
                    label.includes(search) ||
                    clientTypeValue.includes(search) ||
                    binCode.includes(search)
                );
            })
            .slice(0, 8);
    };

    const changeCreateForm = (key, value) => {
        setCreateForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const selectClient = (client) => {
        setSelectedClient(client);
        setClientSearch(client.label || "");
        changeCreateForm("client_id", client.value);
    };

    const clearSelectedClient = () => {
        setSelectedClient(null);
        setClientSearch("");
        changeCreateForm("client_id", "");
    };

    const loadOrders = async (reset = true) => {
        if (reset) {
            setLoading(true);
        } else {
            setMoreLoading(true);
        }

        setError("");
        setMessage("");

        const offset = reset ? 0 : nextOffset;

        const params = {
            tab,
            limit,
            offset: offset || 0,
            ordering,
        };

        if (q.trim()) params.q = q.trim();
        if (orderStatus) params.order_status = orderStatus;
        if (clientId) params.client_id = clientId;
        if (clientType) params.client_type = clientType;
        if (managerId) params.manager_id = managerId;
        if (itemQ.trim()) params.item_q = itemQ.trim();
        if (shopItemId) params.shop_item_id = shopItemId;
        if (minSum) params.min_sum = minSum;
        if (maxSum) params.max_sum = maxSum;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (hasInvoice) params.has_invoice = hasInvoice;
        if (hasPayment) params.has_payment = hasPayment;
        if (hasDeliveryNote) params.has_delivery_note = hasDeliveryNote;

        try {
            const response = await axios.get(ORDERS_URL, { params });
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось загрузить заказы");
                return;
            }

            if (reset) {
                setOrders(data.results || []);
            } else {
                setOrders((prev) => [...prev, ...(data.results || [])]);
            }

            setFilters(data.filters || null);
            setCount(data.count || 0);
            setHasMore(Boolean(data.has_more));
            setNextOffset(data.next_offset);
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setLoading(false);
            setMoreLoading(false);
        }
    };

    const loadCatalogItems = async (reset = true) => {
        if (catalogLoading) return;

        const hasSearch = catalogSearch.trim().length >= 2;
        const hasCategory = Boolean(catalogCategoryId);
        const hasSubCategory = Boolean(catalogSubCategoryId);

        if (!hasSearch && !hasCategory && !hasSubCategory) {
            setCatalogItems([]);
            setCatalogHasMore(false);
            setCatalogNextOffset(null);
            return;
        }

        setCatalogLoading(true);

        const offset = reset ? 0 : catalogNextOffset;

        const params = {
            limit: 8,
            offset: offset || 0,
            ordering: "-id",
        };

        if (catalogSearch.trim()) params.q = catalogSearch.trim();
        if (catalogCategoryId) params.category_id = catalogCategoryId;
        if (catalogSubCategoryId) params.sub_category_id = catalogSubCategoryId;

        try {
            const response = await axios.get(ITEMS_URL, { params });
            const data = response.data;

            if (!data.success) {
                setCreateError(data.message || "Не удалось загрузить товары");
                return;
            }

            if (reset) {
                setCatalogItems(data.results || []);
            } else {
                setCatalogItems((prev) => [...prev, ...(data.results || [])]);
            }

            setCatalogFilters(data.filters || null);
            setCatalogHasMore(Boolean(data.has_more));
            setCatalogNextOffset(data.next_offset);
        } catch (e) {
            setCreateError(e.response?.data?.message || "Ошибка загрузки товаров");
        } finally {
            setCatalogLoading(false);
        }
    };

    const loadCatalogFiltersOnly = async () => {
        try {
            const response = await axios.get(ITEMS_URL, {
                params: {
                    limit: 1,
                    offset: 0,
                    ordering: "-id",
                },
            });

            const data = response.data;

            if (data.success) {
                setCatalogFilters(data.filters || null);
            }
        } catch (e) {
            console.log("Не удалось загрузить фильтры каталога", e);
        }
    };

    const openCreateOrderModal = async () => {
        setCreateModalOpen(true);
        setCreateLoading(true);
        setCreateError("");
        setMessage("");

        setCreateForm({
            client_id: "",
            manager_id: "",
            pay_sum: "",
            recalculate_pay_sum: true,
        });

        setClientSearch("");
        setSelectedClient(null);

        setInvoiceFile(null);
        setPaymentFile(null);
        setDeliveryNoteFile(null);

        setPriceMode("retail");
        setCartItems([]);

        setCatalogItems([]);
        setCatalogFilters(null);
        setCatalogSearch("");
        setCatalogCategoryId("");
        setCatalogSubCategoryId("");
        setCatalogHasMore(false);
        setCatalogNextOffset(null);

        try {
            const response = await axios.get(ORDER_CREATE_URL);
            const data = response.data;

            if (!data.success) {
                setCreateError(data.message || "Не удалось получить форму заказа");
                return;
            }

            const fields = data.fields || {};
            setCreateFields(fields);

            setCreateForm((prev) => ({
                ...prev,
                client_id: "",
                manager_id: fields.manager_id?.value || "",
                pay_sum: "",
                recalculate_pay_sum: true,
            }));

            await loadCatalogFiltersOnly();
        } catch (e) {
            setCreateError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setCreateLoading(false);
        }
    };

    const closeCreateOrderModal = () => {
        if (createSaving) return;

        setCreateModalOpen(false);
        setCreateFields({});
        setCreateError("");

        setClientSearch("");
        setSelectedClient(null);

        setInvoiceFile(null);
        setPaymentFile(null);
        setDeliveryNoteFile(null);

        setCartItems([]);
        setCatalogItems([]);
        setCatalogFilters(null);
        setCatalogSearch("");
        setCatalogCategoryId("");
        setCatalogSubCategoryId("");
    };

    const addItemToCart = (item) => {
        setCartItems((prev) => {
            const exists = prev.find((cartItem) => cartItem.shop_item_id === item.id);

            if (exists) {
                return prev.map((cartItem) => {
                    if (cartItem.shop_item_id !== item.id) return cartItem;

                    return {
                        ...cartItem,
                        quantity: Number(cartItem.quantity || 0) + 1,
                    };
                });
            }

            return [
                ...prev,
                {
                    shop_item_id: item.id,
                    slug: item.slug,
                    ru_name: item.ru_name,
                    name: item.name,
                    image: item.image,
                    price: Number(item.price || 0),
                    opt_price: Number(item.opt_price || 0),
                    quantity: 1,
                    category: item.category,
                    sub_category: item.sub_category,
                },
            ];
        });
    };

    const increaseCartItem = (shopItemId) => {
        setCartItems((prev) =>
            prev.map((item) => {
                if (item.shop_item_id !== shopItemId) return item;

                return {
                    ...item,
                    quantity: Number(item.quantity || 0) + 1,
                };
            })
        );
    };

    const decreaseCartItem = (shopItemId) => {
        setCartItems((prev) =>
            prev
                .map((item) => {
                    if (item.shop_item_id !== shopItemId) return item;

                    return {
                        ...item,
                        quantity: Number(item.quantity || 0) - 1,
                    };
                })
                .filter((item) => Number(item.quantity || 0) > 0)
        );
    };

    const removeCartItem = (shopItemId) => {
        setCartItems((prev) => prev.filter((item) => item.shop_item_id !== shopItemId));
    };

    const submitCreateOrder = async (e) => {
        e.preventDefault();

        if (createSaving) return;

        if (!createForm.client_id) {
            setCreateError("Выберите клиента");
            return;
        }

        if (!createForm.manager_id) {
            setCreateError("Выберите менеджера");
            return;
        }

        if (cartItems.length === 0) {
            setCreateError("Добавьте хотя бы один товар в заказ");
            return;
        }

        setCreateSaving(true);
        setCreateError("");
        setMessage("");

        try {
            const orderItems = cartItems.map((item) => ({
                shop_item_id: item.shop_item_id,
                quantity: Number(item.quantity || 1),
                price: getCartItemPrice(item),
                opt_price: Number(item.opt_price || 0),
            }));

            const formData = new FormData();

            formData.append("client_id", createForm.client_id);
            formData.append("manager_id", createForm.manager_id);
            formData.append("order_status", "Создан");
            formData.append("items", JSON.stringify(orderItems));
            formData.append("recalculate_pay_sum", "true");
            formData.append("pay_sum", getCartTotal());

            if (invoiceFile) {
                formData.append("invoice", invoiceFile);
            }

            if (paymentFile) {
                formData.append("payment", paymentFile);
            }

            if (deliveryNoteFile) {
                formData.append("delivery_note", deliveryNoteFile);
            }

            const response = await axios.post(ORDER_CREATE_URL, formData);
            const data = response.data;

            if (!data.success) {
                setCreateError(data.message || "Не удалось создать заказ");
                return;
            }

            setMessage(data.message || "Заказ создан");
            closeCreateOrderModal();
            await loadOrders(true);
        } catch (e) {
            setCreateError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setCreateSaving(false);
        }
    };

    const resetFilters = () => {
        setQ("");
        setOrderStatus("");
        setClientId("");
        setClientType("");
        setManagerId("");
        setItemQ("");
        setShopItemId("");
        setMinSum("");
        setMaxSum("");
        setDateFrom("");
        setDateTo("");
        setHasInvoice("");
        setHasPayment("");
        setHasDeliveryNote("");
        setOrdering("-id");
        setLimit(20);
    };

    const openDeleteOrderModal = (order) => {
        setDeleteModal({
            open: true,
            id: order.id,
            title: "Удалить заказ?",
            text: `Заказ #${order.id} клиента “${order.client?.name || "—"}” будет удалён. Это действие нельзя случайно отменить.`,
            loading: false,
        });
    };

    const closeDeleteModal = () => {
        if (deleteModal.loading) return;

        setDeleteModal({
            open: false,
            id: null,
            title: "",
            text: "",
            loading: false,
        });
    };

    const confirmDelete = async () => {
        if (!deleteModal.id || deleteModal.loading) return;

        setDeleteModal((prev) => ({
            ...prev,
            loading: true,
        }));

        setError("");
        setMessage("");

        try {
            const response = await axios.post(ORDER_DELETE_URL(deleteModal.id));
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось удалить заказ");
                setDeleteModal((prev) => ({
                    ...prev,
                    loading: false,
                }));
                return;
            }

            setMessage(data.message || "Заказ удалён");
            closeDeleteModal();

            await loadOrders(true);
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка соединения с сервером");

            setDeleteModal((prev) => ({
                ...prev,
                loading: false,
            }));
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            loadOrders(true);
        }, 350);

        return () => clearTimeout(timer);
    }, [
        tab,
        q,
        orderStatus,
        clientId,
        clientType,
        managerId,
        itemQ,
        shopItemId,
        minSum,
        maxSum,
        dateFrom,
        dateTo,
        hasInvoice,
        hasPayment,
        hasDeliveryNote,
        ordering,
        limit,
    ]);

    useEffect(() => {
        if (!createModalOpen) return;

        const timer = setTimeout(() => {
            loadCatalogItems(true);
        }, 350);

        return () => clearTimeout(timer);
    }, [createModalOpen, catalogSearch, catalogCategoryId, catalogSubCategoryId]);

    const renderSelectOptions = (options) => {
        return (options || []).map((item) => (
            <option key={item.value} value={item.value}>
                {item.label}
            </option>
        ));
    };

    const renderFiltersContent = () => {
        return (
            <div className="orders-filter-panel">
                <div className="orders-filter-field orders-filter-search">
                    <span>Поиск</span>
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={filters?.q?.placeholder || "ID, клиент, контакт, товар, статус"}
                    />
                </div>

                <div className="orders-filter-field">
                    <span>Статус</span>
                    <select value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)}>
                        <option value="">Все</option>
                        {renderSelectOptions(filters?.order_status?.options)}
                    </select>
                </div>

                <div className="orders-filter-field">
                    <span>Клиент</span>
                    <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                        <option value="">Все</option>
                        {renderSelectOptions(filters?.client_id?.options)}
                    </select>
                </div>

                <div className="orders-filter-field">
                    <span>Тип клиента</span>
                    <select value={clientType} onChange={(e) => setClientType(e.target.value)}>
                        <option value="">Все</option>
                        {renderSelectOptions(filters?.client_type?.options)}
                    </select>
                </div>

                <div className="orders-filter-field">
                    <span>Менеджер</span>
                    <select value={managerId} onChange={(e) => setManagerId(e.target.value)}>
                        <option value="">Все</option>
                        {renderSelectOptions(filters?.manager_id?.options)}
                    </select>
                </div>

                <div className="orders-filter-field orders-filter-search">
                    <span>Товар</span>
                    <input
                        value={itemQ}
                        onChange={(e) => setItemQ(e.target.value)}
                        placeholder={filters?.item_q?.placeholder || "Название товара"}
                    />
                </div>

                <div className="orders-filter-field">
                    <span>ID товара</span>
                    <input
                        value={shopItemId}
                        onChange={(e) => setShopItemId(e.target.value)}
                        placeholder="15"
                    />
                </div>

                <div className="orders-filter-field">
                    <span>Сумма от</span>
                    <input value={minSum} onChange={(e) => setMinSum(e.target.value)} />
                </div>

                <div className="orders-filter-field">
                    <span>Сумма до</span>
                    <input value={maxSum} onChange={(e) => setMaxSum(e.target.value)} />
                </div>

                <div className="orders-filter-field">
                    <span>Дата от</span>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>

                <div className="orders-filter-field">
                    <span>Дата до</span>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>

                <div className="orders-filter-field">
                    <span>Счет</span>
                    <select value={hasInvoice} onChange={(e) => setHasInvoice(e.target.value)}>
                        {renderSelectOptions(filters?.has_invoice?.options)}
                        {!filters?.has_invoice?.options && (
                            <>
                                <option value="">Все</option>
                                <option value="true">Есть</option>
                                <option value="false">Нет</option>
                            </>
                        )}
                    </select>
                </div>

                <div className="orders-filter-field">
                    <span>Оплата</span>
                    <select value={hasPayment} onChange={(e) => setHasPayment(e.target.value)}>
                        {renderSelectOptions(filters?.has_payment?.options)}
                        {!filters?.has_payment?.options && (
                            <>
                                <option value="">Все</option>
                                <option value="true">Есть</option>
                                <option value="false">Нет</option>
                            </>
                        )}
                    </select>
                </div>

                <div className="orders-filter-field">
                    <span>Накладная</span>
                    <select value={hasDeliveryNote} onChange={(e) => setHasDeliveryNote(e.target.value)}>
                        {renderSelectOptions(filters?.has_delivery_note?.options)}
                        {!filters?.has_delivery_note?.options && (
                            <>
                                <option value="">Все</option>
                                <option value="true">Есть</option>
                                <option value="false">Нет</option>
                            </>
                        )}
                    </select>
                </div>

                <div className="orders-filter-field">
                    <span>Сортировка</span>
                    <select value={ordering} onChange={(e) => setOrdering(e.target.value)}>
                        {renderSelectOptions(filters?.ordering?.options)}
                        {!filters?.ordering?.options && (
                            <>
                                <option value="-id">Сначала новые</option>
                                <option value="id">Сначала старые</option>
                            </>
                        )}
                    </select>
                </div>

                <div className="orders-filter-field orders-filter-limit">
                    <span>Показать</span>
                    <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>

                <button className="orders-filter-reset" type="button" onClick={resetFilters}>
                    Сбросить
                </button>
            </div>
        );
    };

    return (
        <section className="orders-page">
            <div className="orders-head">
                <div>
                    <h1>Заказы</h1>
                    <p>Клиенты, менеджеры, товары, суммы, документы и статусы</p>
                </div>

                <button className="orders-create-btn" type="button" onClick={openCreateOrderModal}>
                    + Новый заказ
                </button>
            </div>

            <div className="orders-tabs">
                <button
                    type="button"
                    className={tab === "all" ? "orders-tab-active" : ""}
                    onClick={() => setTab("all")}
                >
                    Все заказы
                </button>

                <button
                    type="button"
                    className={tab === "my" ? "orders-tab-active" : ""}
                    onClick={() => setTab("my")}
                >
                    Мои заказы
                </button>
            </div>

            <div className="orders-desktop-filters">
                {renderFiltersContent()}
            </div>

            <div className="orders-mobile-filter-row">
                <button type="button" onClick={() => setFiltersOpen(true)}>
                    Фильтры
                </button>

                <span>{count} заказов</span>
            </div>

            {filtersOpen && (
                <div className="orders-filter-modal">
                    <button
                        className="orders-filter-modal-bg"
                        type="button"
                        onClick={() => setFiltersOpen(false)}
                    />

                    <div className="orders-filter-modal-card">
                        <div className="orders-filter-modal-head">
                            <div>
                                <h2>Фильтры</h2>
                                <p>Поиск, статус, клиент, товары и документы</p>
                            </div>

                            <button type="button" onClick={() => setFiltersOpen(false)}>
                                ✕
                            </button>
                        </div>

                        {renderFiltersContent()}

                        <button
                            className="orders-filter-apply"
                            type="button"
                            onClick={() => setFiltersOpen(false)}
                        >
                            Применить
                        </button>
                    </div>
                </div>
            )}

            {error && <div className="orders-error">{error}</div>}
            {message && <div className="orders-success">{message}</div>}

            <div className="orders-list-head">
                <strong>Всего заказов: {count}</strong>
                {loading && <span>Загрузка...</span>}
            </div>

            {loading && <div className="orders-loading">Загрузка заказов...</div>}

            {!loading && orders.length === 0 && (
                <div className="orders-empty">Заказы не найдены</div>
            )}

            {!loading && orders.length > 0 && (
                <div className="orders-list">
                    {orders.map((order) => (
                        <article className="order-card" key={order.id}>
                            <div className="order-card-top">
                                <div>
                                    <span>Заказ #{order.id}</span>
                                    <h2>{order.client?.name || "—"}</h2>
                                    <p>
                                        {order.client?.client_type || "—"} · менеджер: {order.manager?.name || "—"}
                                    </p>
                                </div>

                                <div className={`order-status-badge ${getStatusClass(order.order_status)}`}>
                                    {order.order_status || "—"}
                                </div>
                            </div>

                            <div className="order-card-grid">
                                <div>
                                    <span>Сумма оплаты</span>
                                    <strong>{formatPrice(order.pay_sum)}</strong>
                                </div>

                                <div>
                                    <span>Сумма товаров</span>
                                    <strong>{formatPrice(order.items_sum)}</strong>
                                </div>

                                <div>
                                    <span>Товаров</span>
                                    <strong>{order.items_count || 0}</strong>
                                </div>

                                <div>
                                    <span>Создан</span>
                                    <strong>{order.created_at || "—"}</strong>
                                </div>
                            </div>

                            <div className="order-items-preview">
                                {(order.items_preview || []).length === 0 && (
                                    <div className="order-items-empty">
                                        Товары не указаны
                                    </div>
                                )}

                                {(order.items_preview || []).map((item, index) => (
                                    <div className="order-item-mini" key={`${order.id}-${index}`}>
                                        <div className="order-item-mini-image">
                                            {item.image && item.image !== "—" ? (
                                                <img src={item.image} alt="" />
                                            ) : (
                                                <span>IMG</span>
                                            )}
                                        </div>

                                        <div>
                                            <strong>{item.ru_name || getText(item.name) || "—"}</strong>
                                            <p>
                                                {item.quantity || 0} × {formatPrice(item.price)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="order-documents">
                                <div className={order.has_invoice ? "order-doc-ready" : "order-doc-empty"}>
                                    Счет
                                </div>

                                <div className={order.has_payment ? "order-doc-ready" : "order-doc-empty"}>
                                    Оплата
                                </div>

                                <div className={order.has_delivery_note ? "order-doc-ready" : "order-doc-empty"}>
                                    Накладная
                                </div>
                            </div>

                            <div className="order-card-actions">
                                <button type="button" onClick={() => goToOrderDetails(order.id)}>
                                    Подробнее
                                </button>

                                <button type="button" onClick={() => openDeleteOrderModal(order)}>
                                    Удалить
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {hasMore && (
                <div className="orders-more">
                    <button type="button" onClick={() => loadOrders(false)} disabled={moreLoading}>
                        {moreLoading ? "Загрузка..." : "Показать ещё"}
                    </button>
                </div>
            )}

            {createModalOpen && (
                <div className="order-create-modal">
                    <button className="order-create-modal-bg" type="button" onClick={closeCreateOrderModal} />

                    <div className="order-create-modal-card">
                        <div className="order-create-modal-head">
                            <div>
                                <h2>Новый заказ</h2>
                                <p>Клиент, менеджер, товары из каталога и документы</p>
                            </div>

                            <button type="button" onClick={closeCreateOrderModal}>
                                ✕
                            </button>
                        </div>

                        {createError && <div className="orders-error">{createError}</div>}

                        {createLoading && (
                            <div className="orders-loading">
                                Загрузка формы заказа...
                            </div>
                        )}

                        {!createLoading && (
                            <form className="order-create-form" onSubmit={submitCreateOrder}>
                                <div className="order-create-main-grid">
                                    <div className={selectedClient ? "order-client-picker order-client-picker-selected" : "order-client-picker"}>
                                        <div className="order-client-picker-label">
                                            <span>Клиент</span>

                                            {selectedClient && (
                                                <button type="button" onClick={clearSelectedClient}>
                                                    Сменить
                                                </button>
                                            )}
                                        </div>

                                        <div className="order-client-picker-input">
                                            <input
                                                value={clientSearch}
                                                onChange={(e) => {
                                                    setClientSearch(e.target.value);
                                                    setSelectedClient(null);
                                                    changeCreateForm("client_id", "");
                                                }}
                                                placeholder="Например: ТОО Свет"
                                                disabled={Boolean(selectedClient)}
                                            />
                                        </div>

                                        {selectedClient && (
                                            <div className="order-client-selected-card">
                                                <strong>{selectedClient.label}</strong>
                                                <small>
                                                    {selectedClient.client_type || "—"}
                                                    {selectedClient.bin_code && selectedClient.bin_code !== "—" ? ` · ${selectedClient.bin_code}` : ""}
                                                </small>
                                            </div>
                                        )}

                                        {!selectedClient && clientSearch.trim().length > 0 && (
                                            <div className="order-client-picker-results">
                                                {getFilteredClients().length === 0 && (
                                                    <div className="order-client-picker-empty">
                                                        Клиент не найден
                                                    </div>
                                                )}

                                                {getFilteredClients().map((client) => (
                                                    <button
                                                        type="button"
                                                        key={client.value}
                                                        onClick={() => selectClient(client)}
                                                    >
                                                        <span>{client.label}</span>

                                                        <small>
                                                            {client.client_type || "—"}
                                                            {client.bin_code && client.bin_code !== "—" ? ` · ${client.bin_code}` : ""}
                                                        </small>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <label className="order-create-field">
                                        <span>Менеджер</span>
                                        <select
                                            value={createForm.manager_id}
                                            onChange={(e) => changeCreateForm("manager_id", e.target.value)}
                                        >
                                            <option value="">Выберите менеджера</option>
                                            {renderSelectOptions(getFieldOptions(createFields, "manager_id"))}
                                        </select>
                                    </label>

                                    <div className="order-created-status-fixed">
                                        <span>Статус</span>
                                        <strong>Создан</strong>
                                    </div>

                                    <div className="order-price-mode">
                                        <span>Тип цены</span>

                                        <div>
                                            <button
                                                type="button"
                                                className={priceMode === "retail" ? "order-price-mode-active" : ""}
                                                onClick={() => setPriceMode("retail")}
                                            >
                                                Розница
                                            </button>

                                            <button
                                                type="button"
                                                className={priceMode === "opt" ? "order-price-mode-active" : ""}
                                                onClick={() => setPriceMode("opt")}
                                            >
                                                Опт
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="order-create-content">
                                    <div className="order-catalog-block">
                                        <div className="order-create-section-head">
                                            <div>
                                                <h3>Каталог товаров</h3>
                                                <p>Пиши название товара или выбери категорию</p>
                                            </div>
                                        </div>

                                        <div className="order-catalog-filters">
                                            <label>
                                                <span>Поиск</span>
                                                <input
                                                    value={catalogSearch}
                                                    onChange={(e) => setCatalogSearch(e.target.value)}
                                                    placeholder="Минимум 2 символа"
                                                />
                                            </label>

                                            <label>
                                                <span>Категория</span>
                                                <select
                                                    value={catalogCategoryId}
                                                    onChange={(e) => {
                                                        setCatalogCategoryId(e.target.value);
                                                        setCatalogSubCategoryId("");
                                                    }}
                                                >
                                                    <option value="">Все</option>
                                                    {renderSelectOptions(catalogFilters?.category_id?.options)}
                                                </select>
                                            </label>

                                            <label>
                                                <span>Подкатегория</span>
                                                <select
                                                    value={catalogSubCategoryId}
                                                    onChange={(e) => setCatalogSubCategoryId(e.target.value)}
                                                >
                                                    <option value="">Все</option>
                                                    {renderSelectOptions(catalogFilters?.sub_category_id?.options)}
                                                </select>
                                            </label>
                                        </div>

                                        <div className="order-catalog-list">
                                            {catalogItems.length === 0 && !catalogLoading && (
                                                <div className="order-catalog-empty">
                                                    {catalogSearch.trim().length < 2 && !catalogCategoryId && !catalogSubCategoryId
                                                        ? "Начните писать название товара или выберите категорию"
                                                        : "Товары не найдены"}
                                                </div>
                                            )}

                                            {catalogItems.map((item) => (
                                                <div className="order-catalog-item" key={item.id}>
                                                    <div className="order-catalog-item-image">
                                                        {item.image && item.image !== "—" ? (
                                                            <img src={item.image} alt="" />
                                                        ) : (
                                                            <span>IMG</span>
                                                        )}
                                                    </div>

                                                    <div className="order-catalog-item-info">
                                                        <span>Товар #{item.id}</span>
                                                        <strong>{item.ru_name || getText(item.name)}</strong>
                                                        <p>
                                                            {formatPrice(item.price)} · опт {formatPrice(item.opt_price)}
                                                        </p>
                                                    </div>

                                                    <button type="button" onClick={() => addItemToCart(item)}>
                                                        Добавить
                                                    </button>
                                                </div>
                                            ))}
                                        </div>

                                        {catalogLoading && (
                                            <div className="order-catalog-empty">
                                                Загрузка товаров...
                                            </div>
                                        )}

                                        {catalogHasMore && (
                                            <button
                                                className="order-catalog-more"
                                                type="button"
                                                onClick={() => loadCatalogItems(false)}
                                                disabled={catalogLoading}
                                            >
                                                {catalogLoading ? "Загрузка..." : "Еще товары"}
                                            </button>
                                        )}
                                    </div>

                                    <div className="order-cart-block">
                                        <div className="order-create-section-head">
                                            <div>
                                                <h3>Заказ</h3>
                                                <p>Товары, количество и итоговая сумма</p>
                                            </div>

                                            <strong>{formatPrice(getCartTotal())}</strong>
                                        </div>

                                        {cartItems.length === 0 && (
                                            <div className="order-cart-empty">
                                                Добавь товары из каталога
                                            </div>
                                        )}

                                        {cartItems.length > 0 && (
                                            <div className="order-cart-list">
                                                {cartItems.map((item) => (
                                                    <div className="order-cart-item" key={item.shop_item_id}>
                                                        <div className="order-cart-item-main">
                                                            <div className="order-cart-item-image">
                                                                {item.image && item.image !== "—" ? (
                                                                    <img src={item.image} alt="" />
                                                                ) : (
                                                                    <span>IMG</span>
                                                                )}
                                                            </div>

                                                            <div>
                                                                <strong>{item.ru_name || getText(item.name)}</strong>
                                                                <p>
                                                                    {formatPrice(getCartItemPrice(item))} × {item.quantity}
                                                                </p>
                                                                <small>
                                                                    Итого: {formatPrice(getCartItemPrice(item) * item.quantity)}
                                                                </small>
                                                            </div>
                                                        </div>

                                                        <div className="order-cart-item-controls">
                                                            <button type="button" onClick={() => decreaseCartItem(item.shop_item_id)}>
                                                                −
                                                            </button>

                                                            <strong>{item.quantity}</strong>

                                                            <button type="button" onClick={() => increaseCartItem(item.shop_item_id)}>
                                                                +
                                                            </button>

                                                            <button
                                                                className="order-cart-remove"
                                                                type="button"
                                                                onClick={() => removeCartItem(item.shop_item_id)}
                                                            >
                                                                Убрать
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="order-files-block">
                                            <label>
                                                <span>Счет на оплату</span>
                                                <input
                                                    type="file"
                                                    onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                                                />
                                            </label>

                                            <label>
                                                <span>Подтверждение оплаты</span>
                                                <input
                                                    type="file"
                                                    onChange={(e) => setPaymentFile(e.target.files?.[0] || null)}
                                                />
                                            </label>

                                            <label>
                                                <span>Накладная</span>
                                                <input
                                                    type="file"
                                                    onChange={(e) => setDeliveryNoteFile(e.target.files?.[0] || null)}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="order-create-actions">
                                    <button type="button" onClick={closeCreateOrderModal}>
                                        Отмена
                                    </button>

                                    <button type="submit" disabled={createSaving}>
                                        {createSaving ? "Создаем..." : `Создать заказ на ${formatPrice(getCartTotal())}`}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {deleteModal.open && (
                <div className="delete-modal">
                    <button className="delete-modal-bg" type="button" onClick={closeDeleteModal} />

                    <div className="delete-modal-card">
                        <h2>{deleteModal.title}</h2>
                        <p>{deleteModal.text}</p>

                        <div className="delete-modal-actions">
                            <button type="button" onClick={closeDeleteModal}>
                                Отмена
                            </button>

                            <button type="button" onClick={confirmDelete} disabled={deleteModal.loading}>
                                {deleteModal.loading ? "Удаляем..." : "Да, удалить"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};
